/**
 * "From the Heights" — following the war after our nation has capitulated (free-for-all only;
 * the host enforces who may, see `MatchHost.spectate`). The replay viewer's projection-room
 * chrome around the live map: title bar with the clock and the perspective selector (the whole
 * truth, or any staff's view — each switch asks the host for that stream), the intertitle, the
 * teletype, minimap, hex readout and a read-only unit card. No time controls: the clock belongs
 * to the host — a host who has fallen keeps pause and speeds. When the war ends, the
 * after-action report (our own staff's record) takes over, with The Fog Lifts and Save record.
 */
import { type FactionId, SPECTATOR } from '@friction/engine';
import { type CSSProperties, useEffect, useRef } from 'react';
import { gameStore, useGame } from '../../game/store';
import { useT } from '../../i18n';
import { theatreTitle } from '../../i18n/names';
import { lobbyStore, useLobby } from '../../net/lobby';
import { Button, cx } from '../common/controls';
import { AfterActionReport } from '../game/AfterAction';
import { useMapRenderer } from '../game/GameScreen';
import { HexReadout } from '../game/HexReadout';
import { canControlSpeed } from '../game/hotkeys';
import hud from '../game/hud.module.css';
import { Minimap } from '../game/Minimap';
import { ReconnectBanner } from '../game/Notices';
import { SelectionPanel } from '../game/SelectionPanel';
import { Teletype } from '../game/Teletype';
import { Speed } from '../game/TopBar';
import { Intertitle, PerspectiveBar, ReelClock } from './ReplayScreen';
import s from './replay.module.css';

/** Ask the host for another perspective (V / Shift+V cycle; not once the war is decided). */
function pick(p: FactionId): void {
  const g = gameStore.getState();
  if (!g.view || g.view.winners || g.observing === p) return;
  g.observe(p);
}

function cycle(dir: 1 | -1): void {
  const g = gameStore.getState();
  const ids = [SPECTATOR, ...(g.view?.factions.map((f) => f.id) ?? [])];
  const i = Math.max(0, ids.indexOf(g.observing ?? SPECTATOR));
  pick(ids[(i + dir + ids.length) % ids.length]);
}

function useObserveHotkeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.target instanceof Element && e.target.closest('input, textarea, select')) return;
      const g = gameStore.getState();
      if (g.view?.winners) return; // the after-action report owns the keyboard
      if (e.key === 'Escape') {
        g.select([]);
        g.inspectContact(null);
      } else if (e.key === 'v' || e.key === 'V') cycle(e.shiftKey ? -1 : 1);
      else if (e.key === ' ' && canControlSpeed()) {
        e.preventDefault();
        g.setPaused(!g.paused);
      } else if (/^[1-5]$/.test(e.key) && canControlSpeed()) {
        g.setSpeed(Number(e.key) as 1 | 2 | 3 | 4 | 5);
        if (g.paused) g.setPaused(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

function TitleBar() {
  const t = useT();
  const tick = useGame((g) => g.view?.tick ?? 0);
  const factions = useGame((g) => g.view?.factions);
  const perspective = useGame((g) => g.observing ?? SPECTATOR);
  const name = useGame((g) => g.map?.name ?? '');
  const paused = useGame((g) => g.paused);
  const over = useGame((g) => g.view?.winners != null);
  // Re-evaluated when the room changes (host migration).
  useLobby((l) => l.room?.hostId);
  const host = canControlSpeed();
  return (
    <header className={s.top}>
      <div className={s.brand}>
        <span className={s.brandTitle}>{t('obs.brand')}</span>
        <span className={s.brandSub}>{name && theatreTitle(name, t.locale)}</span>
      </div>
      <ReelClock tick={tick} />
      {factions && <PerspectiveBar nations={factions} perspective={perspective} onPick={pick} />}
      <div className={s.topEnd}>
        {host && !over && <Speed />}
        {!host && paused && !over && <span className={s.pausedChip}>{t('hud.paused')}</span>}
        <Button variant="ghost" icon="exit" onClick={() => lobbyStore.getState().quitGame()}>
          {t('obs.leave')}
        </Button>
      </div>
    </header>
  );
}

function Caption() {
  const perspective = useGame((g) => g.observing ?? SPECTATOR);
  const nation = useGame((g) => g.view?.factions.find((f) => f.id === g.observing));
  return <Intertitle nation={perspective === SPECTATOR ? null : (nation ?? null)} truth={false} />;
}

export function ObserveScreen() {
  const mapRef = useRef<HTMLDivElement>(null);
  useMapRenderer(mapRef);
  useObserveHotkeys();
  const over = useGame((g) => g.view?.winners != null);
  return (
    <div className={hud.screen}>
      <div ref={mapRef} className={hud.map} />
      <div className={hud.vignette} />
      <div className={cx(hud.hud, s.hud)} style={{ '--deck-h': '0px' } as CSSProperties}>
        <TitleBar />
        {!over && <Caption />}
        <div className={cx(hud.status, s.observeStatus)}>
          <ReconnectBanner />
        </div>
        <SelectionPanel />
        <Teletype />
        <HexReadout />
        <Minimap />
        {over && <AfterActionReport />}
      </div>
    </div>
  );
}
